import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const packages = [
  {
    id: 'video-photo-collection',
    name: 'The Wedding Video & Photo Collection',
    tag: 'COMPLETE PACKAGE',
    fromPrice: '3,000',
    monthly: '250',
    highlight: true,
    features: [
      'Two videographers & one photographer',
      'Full day coverage — prep to first dance',
      '20-30 minute cinematic film',
      'Full ceremony, speeches, cake cutting & first dance videos',
      '3-4 minute sneak peek within 72 hours',
      '30-40 sneak peek photos within one week',
      'Online gallery with 500+ photos',
      'No hidden charges. Ever.',
    ],
  },
  {
    id: 'wedding-video-collection',
    name: 'The Wedding Video Collection',
    tag: 'MOST POPULAR',
    fromPrice: '2,295',
    monthly: '191',
    highlight: false,
    features: [
      'Two videographers all day',
      'Full day coverage — prep to first dance',
      '20-30 minute cinematic film',
      'Full ceremony, speeches, cake cutting & first dance videos',
      '3-4 minute sneak peek within 72 hours',
      'Extra videos (singing waiters, etc.) included',
      'No hidden charges. Ever.',
    ],
  },
  {
    id: 'photo-highlight',
    name: 'Photo & Highlight Video',
    tag: null,
    fromPrice: '2,000',
    monthly: '166',
    highlight: false,
    features: [
      'One videographer & one photographer',
      'Up to 8 hours coverage',
      '10-15 minute highlight film',
      '300-500 professionally edited photos',
    ],
  },
  {
    id: 'photo-collection',
    name: 'The Photo Collection',
    tag: null,
    fromPrice: '1,500',
    monthly: '125',
    highlight: false,
    features: [
      'One photographer',
      'Up to 8 hours coverage',
      'Online gallery with 500+ photos',
      '30-40 sneak peek photos within one week',
    ],
  },
];


const Pricing = ({ id }) => {
  const navigate = useNavigate();
  const goToEnquire = () => window.open('https://www.weddingvideo.co.uk/contact', '_blank');

  return (
    <section id={id} data-testid="pricing-section" className="py-24 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-16">
          <p className="text-gold text-sm font-semibold uppercase tracking-widest mb-4">Packages</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white mb-4">Simple pricing. No surprises.</h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Every package includes full digital delivery, no travel charges, and interest-free monthly payments.
          </p>
        </div>

        {/* Top two featured */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {packages.slice(0, 2).map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`package-${pkg.id}`}
              className={`rounded-2xl p-8 border transition-all duration-300 flex flex-col ${
                pkg.highlight
                  ? 'bg-gold/10 border-gold/40 ring-1 ring-gold/20'
                  : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
            >
              {pkg.tag && (
                <span className={`self-start text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4 ${
                  pkg.highlight ? 'bg-gold text-black' : 'bg-white/10 text-white/70'
                }`}>
                  {pkg.tag}
                </span>
              )}

              <h3 className="text-xl font-bold text-white mb-3">{pkg.name}</h3>

              <div className="mb-6">
                <span className="text-3xl font-bold text-white">From £{pkg.fromPrice}</span>
                <span className="text-white/50 text-sm ml-2">or from £{pkg.monthly}/mo</span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pkg.highlight ? 'text-gold' : 'text-white/50'}`} />
                    <span className={feature.includes('72 hours') ? 'text-gold font-semibold' : ''}>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={goToEnquire}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  pkg.highlight
                    ? 'bg-gold hover:bg-gold-light text-black hover:scale-[1.02]'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                Secure My Date <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Bottom two */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {packages.slice(2).map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`package-${pkg.id}`}
              className="rounded-2xl p-6 border border-white/10 bg-white/5 hover:border-white/20 transition-all duration-300 flex flex-col"
            >
              <h3 className="text-lg font-bold text-white mb-1">{pkg.name}</h3>
              <div className="mb-4">
                <span className="text-2xl font-bold text-white">From £{pkg.fromPrice}</span>
                <span className="text-white/50 text-sm ml-2">or from £{pkg.monthly}/mo</span>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {pkg.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/40" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={goToEnquire}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
              >
                Secure My Date <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Payment logos */}
        <div className="mt-12 text-center">
          <p className="text-white/40 text-sm mb-4">Interest-free monthly payments available on all packages</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <i className="fa-brands fa-cc-visa text-3xl text-white/50 hover:text-white transition-colors" title="Visa"></i>
            <i className="fa-brands fa-cc-mastercard text-3xl text-white/50 hover:text-white transition-colors" title="Mastercard"></i>
            <i className="fa-brands fa-cc-amex text-3xl text-white/50 hover:text-white transition-colors" title="American Express"></i>
            <i className="fa-brands fa-cc-apple-pay text-3xl text-white/50 hover:text-white transition-colors" title="Apple Pay"></i>
            <i className="fa-brands fa-google-pay text-3xl text-white/50 hover:text-white transition-colors" title="Google Pay"></i>
            <span className="text-white/50 hover:text-white text-sm font-bold border border-white/20 rounded px-2 py-0.5 transition-colors" title="Klarna">Klarna</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
